import { useParams } from "react-router-dom";
import { useGetDictamenesQuery } from "../../../services/docs";
import { Spinner } from "../../../components/Spinner";

export const DictamenesScreen = () => {
  const { siniestro = "0", tramite_id = "0" } = useParams();

  const { data, isLoading, isError } = useGetDictamenesQuery({
    siniestro,
    tramite: tramite_id,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const dictamenes = data?.dictamenes || [];
  const hasDictamenes = !isError && dictamenes.length > 0;

  return (
    <div className="space-y-6 pt-2">

      {/* --- ESTADO VACÍO O ERROR --- */}
      {!hasDictamenes && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/50 py-12 text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
          </div>
          <p className="text-sm font-bold text-slate-600">Dictamen no disponible</p>
          <p className="mt-1 text-xs text-slate-400">
            {isError ? "Ocurrió un error al consultar el dictamen." : "Aún no se ha registrado una resolución oficial para este trámite."}
          </p>
        </div>
      )}

      {/* --- LISTA DE DICTÁMENES --- */}
      {hasDictamenes && (
        <div className="grid grid-cols-1 gap-6">
          {dictamenes.map((dic: any) => (
            <article key={dic.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">

              {/* Cabecera del Dictamen */}
              <div className="border-b border-slate-100 bg-slate-50/80 px-6 py-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                    <div className="flex h-6 w-6 items-center justify-center rounded bg-brand-100 text-brand-700">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                    </div>
                    Resolución Oficial #{dic.id}
                  </h3>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500 shadow-sm">
                    <svg className="h-3.5 w-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                    Emitido: {dic.fd_fecha_dictamen || "N/D"}
                  </span>
                </div>
              </div>

              {/* Cuerpo de la Tarjeta */}
              <div className="p-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">

                  {/* Bloque 1: Datos Aseguradora */}
                  <div className="lg:col-span-2">
                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Respuesta de la Aseguradora
                    </span>
                    <p className="text-lg font-black text-slate-900">
                      {dic.fc_respuesta_aseguradora || "Sin respuesta registrada"}
                    </p>

                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Siniestro Aseguradora
                        </span>
                        <p className="text-sm font-semibold text-slate-800">
                          {dic.fc_siniestro_aseguradora || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Monto Pagado
                        </span>
                        <p className="text-sm font-bold text-emerald-600">
                          {dic.fn_monto_pagado ? `$ ${dic.fn_monto_pagado}` : "N/A"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Comentarios del Dictamen
                      </span>
                      <p className="rounded-lg bg-slate-50 p-3 text-sm font-medium leading-relaxed text-slate-700 border border-slate-100">
                        {dic.fc_comentarios_dictamen || "Sin comentarios."}
                      </p>
                    </div>
                  </div>

                  {/* Bloque 2: Datos Auditoría */}
                  <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                    <div className="mb-3 flex items-center gap-1.5 border-b border-slate-200 pb-2">
                      <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">Evaluación de Auditoría</h4>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <span className="block text-[10px] font-semibold uppercase text-slate-400">Resolución</span>
                        <span className={`mt-1 inline-block rounded-md px-2 py-0.5 text-xs font-bold ${dic.fc_resolucion_auditoria?.toLowerCase().includes("incorrecto")
                            ? "bg-rose-100 text-rose-700 border border-rose-200"
                            : "bg-emerald-100 text-emerald-700 border border-emerald-200"
                          }`}>
                          {dic.fc_resolucion_auditoria || "Pendiente"}
                        </span>
                      </div>

                      <div>
                        <span className="block text-[10px] font-semibold uppercase text-slate-400">Fecha Auditoría</span>
                        <span className="mt-0.5 block text-xs font-medium text-slate-700">
                          {dic.fd_fecha_auditoria || "No realizada"}
                        </span>
                      </div>

                      <div>
                        <span className="block text-[10px] font-semibold uppercase text-slate-400">Comentarios</span>
                        <p className="mt-0.5 text-xs text-slate-600">
                          {dic.fc_comentarios_auditoria || "Sin observaciones registradas."}
                        </p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Footer: Acción */}
              <div className="flex items-center justify-end border-t border-slate-100 bg-slate-50/50 p-4">
                {dic.fc_url_carta ? (
                  <a
                    href={dic.fc_url_carta}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-brand-600 shadow-sm transition hover:bg-slate-50 hover:text-brand-700"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                    Ver Carta Dictamen
                  </a>
                ) : (
                  <span className="text-[11px] font-medium text-slate-400">Carta dictamen no disponible</span>
                )}
              </div>

            </article>
          ))}
        </div>
      )}

    </div>
  );
};
